import React, { useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Download,
  Upload,
  FileSpreadsheet,
  AlertCircle,
  CheckCircle2,
  Loader2,
  Info,
  ArrowRight,
  TriangleAlert,
  X,
} from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import {
  exportClinicProducts,
  previewBulkUpdateFromExcel,
  bulkUpdateClinicProductsAsync,
  type BulkPriceUpdate,
} from '@/services/products.service';
import { BulkUpdateConfirmDialog } from '@/components/clinic/BulkUpdateConfirmDialog';
import { useClinic } from '@/contexts/ClinicContext';

interface ExcelRow {
  id: string;
  description: string;
  ndc: string;
  base_price_dollars: number;
  selling_price_dollars: number;
}

interface PreviewRow extends ExcelRow {
  current_selling_price: number;
  new_selling_price: number;
  new_selling_price_str: string;
  hasChanged: boolean;
  hasError: boolean;
  errorMessage?: string;
}

interface BulkUpdateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentSearch: string;
  onSuccess: () => void;
}

type DialogState = 'initial' | 'preview' | 'submitting' | 'results';

const BulkUpdateDialog: React.FC<BulkUpdateDialogProps> = ({
  open,
  onOpenChange,
  currentSearch,
  onSuccess,
}) => {
  const { user } = useAuth();
  const { clinic } = useClinic();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [state, setState] = useState<DialogState>('initial');
  const [isDownloading, setIsDownloading] = useState(false);
  const [previewData, setPreviewData] = useState<PreviewRow[]>([]);
  const [results, setResults] = useState<{
    updated: number;
    errors: Array<{ id: string; description?: string; error: string }>;
  } | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingUpdates, setPendingUpdates] = useState<BulkPriceUpdate[]>([]);

  const clinicId = clinic?.id;

  const handleDownload = async () => {
    if (!clinicId) {
      toast({
        title: 'Error',
        description: 'Clinic ID not found',
        variant: 'destructive',
      });
      return;
    }

    try {
      setIsDownloading(true);
      const blob = await exportClinicProducts(clinicId, currentSearch);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `product-pricing-${Date.now()}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast({
        title: 'Download started',
        description: 'Your Excel file is being downloaded',
        variant: 'success',
      });
    } catch (error: any) {
      console.error('Error downloading:', error);
      toast({
        title: 'Download failed',
        description: error.message || 'Failed to download Excel file',
        variant: 'destructive',
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const handleFileSelect = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      if (!clinicId) {
        toast({
          title: 'Error',
          description: 'Clinic ID not found',
          variant: 'destructive',
        });
        return;
      }

      try {
        setState('submitting');

        const preview = await previewBulkUpdateFromExcel(clinicId, file);

        setPreviewData(
          (preview as PreviewRow[]).map((row) => ({
            ...row,
            new_selling_price_str: row.new_selling_price > 0 ? String(row.new_selling_price) : '',
          }))
        );
        setState('preview');
      } catch (error: any) {
        console.error('Error previewing Excel:', error);
        toast({
          title: 'Preview failed',
          description: error?.message || 'Failed to preview the Excel file. Please check the format.',
          variant: 'destructive',
        });
        setState('initial');
      }

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [clinicId]
  );

  const handlePriceChange = (id: string, value: string) => {
    setPreviewData((prev) =>
      prev.map((row) => {
        if (row.id !== id) return row;

        // Validate input format: digits with optional decimal up to 2 places
        if (value !== '' && !/^\d*\.?\d{0,2}$/.test(value)) return row;

        const newPrice = parseFloat(value) || 0;
        const hasError = newPrice <= row.base_price_dollars || newPrice > 10000;
        let errorMessage: string | undefined;

        if (newPrice <= row.base_price_dollars) {
          errorMessage = 'Selling price must be greater than base price';
        } else if (newPrice > 10000) {
          errorMessage = 'Selling price cannot exceed $10,000';
        }

        // Compare against original price (stored in current_selling_price)
        const hasChanged = newPrice !== row.current_selling_price && !hasError;

        return {
          ...row,
          new_selling_price: newPrice,
          new_selling_price_str: value,
          hasChanged,
          hasError,
          errorMessage,
        };
      })
    );
  };

  const calculateMargin = (sellingPrice: number, basePrice: number) => {
    const amount = sellingPrice - basePrice;
    let percentage = 0;

    if (basePrice === 0 && sellingPrice > 0) {
      percentage = 100;
    } else if (basePrice > 0) {
      percentage = (amount / basePrice) * 100;
    }

    return { amount, percentage };
  };

  const handleSubmit = async () => {
    if (!clinicId) {
      toast({
        title: 'Error',
        description: 'Clinic ID not found',
        variant: 'destructive',
      });
      return;
    }

    const changedRows = previewData.filter((row) => row.hasChanged && !row.hasError);

    if (changedRows.length === 0) {
      toast({
        title: 'No changes',
        description: 'No valid price changes to apply',
        variant: 'destructive',
      });
      return;
    }

    const updates: BulkPriceUpdate[] = changedRows.map((row) => ({
      id: row.id,
      selling_price_dollars: row.new_selling_price,
    }));

    setPendingUpdates(updates);
    setShowConfirmDialog(true);
  };

  const handleConfirmSubmit = async (notificationEmail: string) => {
    if (!clinicId) {
      toast({
        title: 'Error',
        description: 'Clinic ID not found',
        variant: 'destructive',
      });
      return;
    }

    try {
      setShowConfirmDialog(false);
      setState('submitting');

      const response = await bulkUpdateClinicProductsAsync(clinicId, {
        updates: pendingUpdates,
        notificationEmail,
      });

      toast({
        title: 'Update queued',
        description:
          response.message || 'Bulk update queued. You will receive an email when complete.',
      });

      onSuccess();
      handleClose();
    } catch (error: any) {
      console.error('Error queuing price update:', error);
      toast({
        title: 'Queueing failed',
        description: error.message || 'Failed to queue price updates',
        variant: 'destructive',
      });
      setState('preview');
    }
  };

  const handleClose = () => {
    setState('initial');
    setPreviewData([]);
    setResults(null);
    setPendingUpdates([]);
    setShowConfirmDialog(false);
    onOpenChange(false);
  };

  const changedCount = previewData.filter((r) => r.hasChanged).length;
  const errorCount = previewData.filter((r) => r.hasError).length;
  const validChangesCount = previewData.filter(
    (r) => r.hasChanged && !r.hasError
  ).length;

  return (
    <Sheet open={open} onOpenChange={handleClose}>
      <SheetContent className="w-full !max-w-[840px] p-0 flex flex-col gap-0 [&>button]:hidden">
        {/* Header */}
        <SheetHeader className="flex-row items-center justify-between px-6 py-4 border-b border-slate-200 space-y-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-brand-copper/10 flex items-center justify-center">
              <FileSpreadsheet className="h-5 w-5 text-brand-copper" />
            </div>
            <div>
              <SheetTitle className="text-lg font-semibold text-slate-900 text-left">
                Bulk Price Update
              </SheetTitle>
              <SheetDescription className="text-sm text-slate-500 mt-0.5 text-left">
                {state === 'initial' && 'Download, update prices in Excel, and upload to preview changes'}
                {state === 'preview' && 'Review and confirm your price changes'}
                {state === 'submitting' && 'Applying your changes...'}
                {state === 'results' && 'Update complete'}
              </SheetDescription>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="w-10 h-10 rounded-full border border-slate-200 flex items-center justify-center hover:bg-slate-50 transition-colors"
          >
            <X className="h-5 w-5 text-slate-600" />
          </button>
        </SheetHeader>

        <div className={`flex-1 min-h-0 px-6 py-6 ${state === 'preview' ? 'flex flex-col overflow-hidden' : 'overflow-auto'}`}>
          <AnimatePresence mode="wait" initial={false}>
            {/* Initial State */}
            {state === 'initial' && (
              <motion.div
                key="initial"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
                className="space-y-4"
              >
                {/* Instructions */}
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <h3 className="font-semibold text-slate-800 flex items-center gap-2 text-xs">
                    <Info className="h-3.5 w-3.5 text-brand-teal" />
                    How to bulk update prices
                  </h3>
                  <div className="mt-2 space-y-1">
                    <div className="flex items-start gap-2 text-xs">
                      <span className="w-4 h-4 rounded-full bg-brand-teal text-white flex items-center justify-center text-[10px] font-semibold shrink-0">1</span>
                      <span className="text-slate-600">
                        Click <span className="font-medium text-slate-800">Download Excel</span> to get your current product list
                        {currentSearch && <span className="text-brand-teal"> (filtered: "{currentSearch}")</span>}
                      </span>
                    </div>
                    <div className="flex items-start gap-2 text-xs">
                      <span className="w-4 h-4 rounded-full bg-brand-teal text-white flex items-center justify-center text-[10px] font-semibold shrink-0">2</span>
                      <span className="text-slate-600">
                        Open the file and update the <span className="font-medium text-slate-800">Selling Price ($)</span> column
                      </span>
                    </div>
                    <div className="flex items-start gap-2 text-xs">
                      <span className="w-4 h-4 rounded-full bg-brand-teal text-white flex items-center justify-center text-[10px] font-semibold shrink-0">3</span>
                      <span className="text-slate-600">Upload the modified file to preview and apply changes</span>
                    </div>
                  </div>
                </div>

                {/* Action Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {/* Download Card */}
                  <div className="border border-slate-200 rounded-lg p-4 hover:border-brand-teal/50 hover:shadow-sm transition-all">
                    <div className="flex flex-col items-center text-center">
                      <div className="w-12 h-12 rounded-xl bg-brand-teal/10 flex items-center justify-center mb-3">
                        <Download className="h-6 w-6 text-brand-teal" />
                      </div>
                      <h4 className="font-semibold text-slate-900 text-sm">Step 1: Download</h4>
                      <p className="text-xs text-slate-500 mt-0.5 mb-3">Get your current product pricing</p>
                      <Button
                        onClick={handleDownload}
                        disabled={isDownloading}
                        size="sm"
                        className="w-full bg-brand-teal hover:bg-brand-teal/90"
                      >
                        {isDownloading ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Downloading...
                          </>
                        ) : (
                          <>
                            <Download className="h-4 w-4 mr-2" />
                            Download Excel
                          </>
                        )}
                      </Button>
                    </div>
                  </div>

                  {/* Upload Card */}
                  <div className="border border-slate-200 rounded-lg p-4 hover:border-brand-copper/50 hover:shadow-sm transition-all">
                    <div className="flex flex-col items-center text-center">
                      <div className="w-12 h-12 rounded-xl bg-brand-copper/10 flex items-center justify-center mb-3">
                        <Upload className="h-6 w-6 text-brand-copper" />
                      </div>
                      <h4 className="font-semibold text-slate-900 text-sm">Step 2: Upload</h4>
                      <p className="text-xs text-slate-500 mt-0.5 mb-3">Upload your modified Excel file</p>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".xlsx,.xls"
                        onChange={handleFileSelect}
                        className="hidden"
                        id="excel-upload"
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full border-brand-copper/30 text-brand-copper hover:bg-brand-copper/5 hover:text-brand-copper"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        Upload Excel
                      </Button>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Preview State */}
            {state === 'preview' && (
              <motion.div
                key="preview"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
                className="flex flex-col h-full"
              >
                {/* Top Section - Summary & Warning */}
                <div className="space-y-3 mb-4">
                  {/* Summary Bar */}
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="outline" className="gap-1.5 bg-slate-50 text-slate-700 border-slate-200">
                      <FileSpreadsheet className="h-3 w-3" />
                      {previewData.length} products
                    </Badge>
                    {changedCount > 0 && (
                      <Badge variant="outline" className="gap-1.5 bg-brand-teal/5 text-brand-teal border-brand-teal/20">
                        <ArrowRight className="h-3 w-3" />
                        {changedCount} changed
                      </Badge>
                    )}
                    {errorCount > 0 && (
                      <Badge variant="outline" className="gap-1.5 bg-red-50 text-red-600 border-red-200">
                        <AlertCircle className="h-3 w-3" />
                        {errorCount} errors
                      </Badge>
                    )}
                    {validChangesCount > 0 && (
                      <Badge variant="outline" className="gap-1.5 bg-emerald-50 text-emerald-700 border-emerald-200">
                        <CheckCircle2 className="h-3 w-3" />
                        {validChangesCount} ready
                      </Badge>
                    )}
                  </div>

                  {/* Warning - Moved to top */}
                  {errorCount > 0 && (
                    <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
                      <TriangleAlert className="h-3.5 w-3.5 shrink-0" />
                      <span>{errorCount} product(s) have invalid prices and will be skipped</span>
                    </div>
                  )}
                </div>

                {/* Table - Flex grow to fill space */}
                <div className="flex-1 min-h-0 border border-slate-200 rounded-xl overflow-hidden">
                  <div className="h-full overflow-auto">
                    <table className="w-full text-sm">
                      <thead style={{ backgroundColor: 'rgb(245, 247, 247)' }} className="sticky top-0 z-10">
                        <tr className="border-b border-slate-200">
                          <th className="text-left px-4 py-2.5 font-semibold text-slate-700 text-xs uppercase tracking-wider">Product</th>
                          <th className="text-right px-4 py-2.5 font-semibold text-slate-700 text-xs uppercase tracking-wider whitespace-nowrap">Base Price</th>
                          <th className="text-right px-4 py-2.5 font-semibold text-slate-700 text-xs uppercase tracking-wider">Current</th>
                          <th className="text-right px-4 py-2.5 font-semibold text-slate-700 text-xs uppercase tracking-wider whitespace-nowrap">New Price</th>
                          <th className="text-right px-4 py-2.5 font-semibold text-slate-700 text-xs uppercase tracking-wider">Margin</th>
                        </tr>
                      </thead>
                      <tbody>
                        {previewData.map((row, index) => {
                          const basePrice = Number(row.base_price_dollars) || 0;
                          const currentPrice = Number(row.current_selling_price) || 0;
                          const newPrice = Number(row.new_selling_price) || 0;
                          const margin = calculateMargin(newPrice, basePrice);
                          return (
                            <tr
                              key={row.id || `preview-row-${index}`}
                              className={`border-b border-slate-100 ${
                                row.hasError ? 'bg-red-50' : row.hasChanged ? 'bg-brand-teal/5' : ''
                              }`}
                            >
                              <td className="px-4 py-3 max-w-[200px]">
                                <div className="truncate font-medium text-slate-900 text-[13px]" title={row.description}>
                                  {row.description || '-'}
                                </div>
                                <div className="text-xs text-slate-500">{row.ndc || '-'}</div>
                              </td>
                              <td className="px-4 py-3 text-right text-slate-600 text-[13px]">${basePrice.toFixed(2)}</td>
                              <td className="px-4 py-3 text-right text-slate-500 text-[13px]">${currentPrice.toFixed(2)}</td>
                              <td className="px-4 py-3">
                                <div className="flex items-center justify-end gap-1.5">
                                  <span className="text-slate-400 text-xs">$</span>
                                  <Input
                                    type="text"
                                    inputMode="decimal"
                                    value={row.new_selling_price_str}
                                    onChange={(e) => handlePriceChange(row.id, e.target.value)}
                                    className={`w-20 h-8 text-right text-[13px] ${
                                      row.hasError ? 'border-red-400 focus:border-red-500' : row.hasChanged ? 'border-brand-teal' : ''
                                    }`}
                                  />
                                  {row.hasError && (
                                    <div className="relative group">
                                      <TriangleAlert className="h-4 w-4 text-red-500" />
                                      <div className="absolute right-0 top-6 bg-slate-900 text-white text-xs p-2 rounded-lg shadow-lg hidden group-hover:block whitespace-nowrap z-10">
                                        {row.errorMessage}
                                      </div>
                                    </div>
                                  )}
                                  {row.hasChanged && !row.hasError && (
                                    <CheckCircle2 className="h-4 w-4 text-brand-teal" />
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-3 text-right">
                                <span className={`font-medium text-[13px] ${margin.amount < 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                                  ${margin.amount.toFixed(2)}
                                </span>
                                <span className="text-xs text-slate-400 ml-1">({margin.percentage.toFixed(1)}%)</span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Action Buttons - Fixed at bottom */}
                <div className="flex items-center justify-end gap-3 pt-4 mt-auto">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setState('initial');
                      setPreviewData([]);
                    }}
                    className="rounded-lg"
                  >
                    Back
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleSubmit}
                    disabled={validChangesCount === 0}
                    className="bg-brand-teal hover:bg-brand-teal/90 rounded-lg gap-2"
                  >
                    <CheckCircle2 className="h-4 w-4" />
                    Apply {validChangesCount} Change(s)
                  </Button>
                </div>
              </motion.div>
            )}

            {/* Submitting State */}
            {state === 'submitting' && (
              <motion.div
                key="submitting"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex flex-col items-center justify-center py-16"
              >
                <div className="w-16 h-16 rounded-2xl bg-brand-teal/10 flex items-center justify-center mb-4">
                  <Loader2 className="h-8 w-8 text-brand-teal animate-spin" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900">Updating prices...</h3>
                <p className="text-slate-500 text-sm mt-1">Please wait while we apply your changes</p>
              </motion.div>
            )}

            {/* Results State */}
            {state === 'results' && results && (
              <motion.div
                key="results"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
                className="space-y-4"
              >
                {/* Success Summary */}
                {results.updated > 0 && (
                  <div className="flex items-center gap-4 p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
                    <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center shrink-0">
                      <CheckCircle2 className="h-6 w-6 text-emerald-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-emerald-900">
                        Successfully updated {results.updated} product(s)
                      </h4>
                      <p className="text-sm text-emerald-700 mt-0.5">Your price changes have been applied</p>
                    </div>
                  </div>
                )}

                {/* Errors */}
                {results.errors.length > 0 && (
                  <div className="space-y-3">
                    <h4 className="font-semibold text-red-800 flex items-center gap-2 text-sm">
                      <AlertCircle className="h-4 w-4" />
                      {results.errors.length} error(s) occurred
                    </h4>
                    <div className="max-h-[180px] overflow-auto border border-red-200 rounded-xl">
                      {results.errors.map((error, index) => (
                        <div
                          key={`error-${error.id || index}`}
                          className="px-4 py-3 border-b border-red-100 last:border-b-0 bg-red-50 text-sm"
                        >
                          <span className="font-medium text-red-900">{error.description || error.id}:</span>{' '}
                          <span className="text-red-700">{error.error}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Action Button */}
                <div className="flex items-center justify-end pt-2">
                  <Button size="sm" onClick={handleClose} className="bg-brand-teal hover:bg-brand-teal/90 rounded-lg">
                    Done
                  </Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </SheetContent>
      <BulkUpdateConfirmDialog
        open={showConfirmDialog}
        onOpenChange={setShowConfirmDialog}
        onConfirm={handleConfirmSubmit}
        defaultEmail={user?.email || ''}
        updateCount={pendingUpdates.length}
        priceType="selling"
      />
    </Sheet>
  );
};

export default BulkUpdateDialog;

